import { Grid } from '@mui/material';
import type { BillingCycleInvoiceType } from '@vy/api-definition';
import { BILLING_CYCLE_INVOICE_TYPES } from '@vy/api-definition';
import { fromNullToUndefined, withDefault } from '@vy/utils';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { NavLink } from 'react-router-dom';
import { VyPageLayout } from '../../components/new-design/layout/VyPageLayout';
import { withGlobalSearchParams } from '../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../components/utility/SuspenseBoundary';
import { useBillingCycleInvoices } from '../../hooks/requests/use-billing-cycle-invoices/use-billing-cycle-invoices';
import { useBillingCyclePage } from '../../hooks/requests/use-billing-cycle-page';
import { useGetApprovalFlowInvoicesList } from '../../hooks/requests/use-get-approval-flow-invoices-list';
import { useTablePagination } from '../../hooks/use-table-pagination';
import { useDebounce } from '../../hooks/use-debounce';
import { useQueryInputParams } from '../invoices/hooks/use-query-input-param';
import { BillingCycleSidebar } from './billing-cycle-components/BillingCycleSidebar';
import { BillingCycleTable } from './billing-cycle-components/BillingCycleTable';

const BillingCyclePage: React.FC = () => {
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<BillingCycleInvoiceType>(
    BILLING_CYCLE_INVOICE_TYPES.REGULAR,
  );

  const { page, pageSize, onPageChange, onPageSizeChange, setPage } = useTablePagination();
  const [query] = useQueryInputParams();
  const search = fromNullToUndefined(query);
  const debouncedSearch = useDebounce(search);

  const { data: billingCyclePage, isFetching: isFetchingBillingCyclePage } = useBillingCyclePage({
    search: debouncedSearch,
  });

  const regularBillingCycleInvoicesData = useBillingCycleInvoices(
    {
      page,
      pageSize,
      search: debouncedSearch,
      billingCycleInvoiceType: BILLING_CYCLE_INVOICE_TYPES.REGULAR,
    },
    {
      enabled: selectedInvoiceType === BILLING_CYCLE_INVOICE_TYPES.REGULAR,
    },
  );
  const inspectionBillingCycleInvoicesData = useBillingCycleInvoices(
    {
      page,
      pageSize,
      search: debouncedSearch,
      billingCycleInvoiceType: BILLING_CYCLE_INVOICE_TYPES.INSPECTION,
    },
    {
      enabled: selectedInvoiceType === BILLING_CYCLE_INVOICE_TYPES.INSPECTION,
    },
  );
  const oldBillingCycleInvoicesData = useBillingCycleInvoices(
    {
      page,
      pageSize,
      search: debouncedSearch,
      billingCycleInvoiceType: BILLING_CYCLE_INVOICE_TYPES.OLD,
    },
    {
      enabled: selectedInvoiceType === BILLING_CYCLE_INVOICE_TYPES.OLD,
    },
  );

  const regularBillingCycleInvoices = withDefault(regularBillingCycleInvoicesData.data?.invoices, []);
  const inspectionBillingCycleInvoices = withDefault(inspectionBillingCycleInvoicesData.data?.invoices, []);
  const oldBillingCycleInvoices = withDefault(oldBillingCycleInvoicesData.data?.invoices, []);

  useGetApprovalFlowInvoicesList(['previousCycle']);

  return (
    <>
      <Helmet>
        <title>Monthly Closure</title>
      </Helmet>
      <VyPageLayout
        path={[<NavLink to="/invoices">Invoices</NavLink>, 'Monthly Closure']}
        title="Monthly Closure"
      >
        <Grid container className="flex h-full" spacing={2}>
          <Grid
            size={{
              xs: 12,
              lg: 3
            }}>
            <BillingCycleSidebar
              selectedInvoiceType={selectedInvoiceType}
              onInvoicePreviewClick={(selected) => {
                setSelectedInvoiceType(selected);
                setPage(0);
              }}
              regularBillingCycleInvoicesCount={withDefault(
                billingCyclePage?.regularBillingCycleInvoicesCount,
                0,
              )}
              inspectionBillingCycleInvoicesCount={withDefault(
                billingCyclePage?.inspectionBillingCycleInvoicesCount,
                0,
              )}
              oldBillingCycleInvoicesCount={withDefault(billingCyclePage?.oldBillingCycleInvoicesCount, 0)}
              overcharges={withDefault(billingCyclePage?.overcharges, 0)}
              loading={isFetchingBillingCyclePage}
            />
          </Grid>

          <Grid
            className="h-full"
            size={{
              xs: 12,
              lg: 9
            }}
            sx={{
              overflow: "hidden"
            }}>
            {selectedInvoiceType === BILLING_CYCLE_INVOICE_TYPES.REGULAR && (
              <BillingCycleTable
                tableName="billing-cycle-regular"
                items={regularBillingCycleInvoices}
                itemsCount={withDefault(billingCyclePage?.regularBillingCycleInvoicesCount, 0)}
                loadingItems={regularBillingCycleInvoicesData.isFetching}
                page={page}
                pageSize={pageSize}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
              />
            )}

            {selectedInvoiceType === BILLING_CYCLE_INVOICE_TYPES.INSPECTION && (
              <BillingCycleTable
                tableName="billing-cycle-inspection"
                items={inspectionBillingCycleInvoices}
                itemsCount={withDefault(billingCyclePage?.inspectionBillingCycleInvoicesCount, 0)}
                loadingItems={inspectionBillingCycleInvoicesData.isFetching}
                page={page}
                pageSize={pageSize}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
              />
            )}

            {selectedInvoiceType === BILLING_CYCLE_INVOICE_TYPES.OLD && (
              <BillingCycleTable
                tableName="billing-cycle-old"
                items={oldBillingCycleInvoices}
                itemsCount={withDefault(billingCyclePage?.oldBillingCycleInvoicesCount, 0)}
                loadingItems={oldBillingCycleInvoicesData.isFetching}
                page={page}
                pageSize={pageSize}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
              />
            )}
          </Grid>
        </Grid>
      </VyPageLayout>
    </>
  );
};

const BillingCyclePageWithSuspenseBoundary = withSuspenseBoundary(withGlobalSearchParams(BillingCyclePage));
export { BillingCyclePageWithSuspenseBoundary as BillingCyclePage };
